import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { queryClient } from './queryClient'
import { AuthProvider } from './context/AuthContext'

/**
 * Composable root providers for the app.
 *
 * Kept as its own component (instead of being inlined into `main.jsx`) so
 * cross-cutting providers can be added/reviewed independently of the router
 * setup and other root wiring being done in parallel tasks. `main.jsx`
 * should simply wrap its tree with `<AppProviders>...</AppProviders>`.
 *
 * Renders the React Query Devtools panel only in dev builds
 * (`import.meta.env.DEV`), so it's never bundled/shown in production.
 *
 * `AuthProvider` (task 009, `context/AuthContext.jsx`) is nested inside
 * `QueryClientProvider` — it doesn't depend on React Query, but query hooks
 * added by feature modules may need `useAuth()` (e.g. to gate a query on
 * `isAuthenticated()`), so it must be available at the same level or above.
 *
 * `prop-types` isn't a project dependency (JS-only scaffold, no runtime
 * prop validation elsewhere yet — see `RoutePlaceholder.jsx`), so the
 * `react/prop-types` lint rule is disabled here for the same reason.
 */
// eslint-disable-next-line react/prop-types
function AppProviders({ children }) {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>{children}</AuthProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  )
}

export default AppProviders
